import { generateURL } from '@Globals/Algolia/Fetcher'
import { AlgoliaProductV2 } from '@Globals/Algolia/Types'

interface AlgoliaHighlightResult {
  fullyHighlighted: boolean
  matchLevel: 'full'
  matchedWords: string[]
  value: string
}

interface AlgoliaCommon {
  _highlightResult: Record<string, AlgoliaHighlightResult>
  objectID: string
}

export interface AlgoliaBrand extends AlgoliaCommon {
  slug: string
  title: string
}

export interface AlgoliaCategory extends AlgoliaCommon {
  depth: number
  parentTitle: string
  slug: string
  title: string
}

export type AlgoliaProduct = AlgoliaProductV2 & AlgoliaCommon

export interface SearchResults {
  brands: AlgoliaBrand[]
  categories: AlgoliaCategory[]
  products: AlgoliaProduct[]
}

// Algolia is caching requests (not responses), so multiple calls to search with the same parameters
// will not result in excessive network requests to algolia
export const search = async (locale: string, query: string): Promise<SearchResults> => {
  if (!query) {
    return {
      brands: [],
      categories: [],
      products: [],
    }
  }

  const algoliaLocale = locale.toLowerCase().replace('-', '_')

  const url = generateURL()
  const res = await fetch(url, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json; charset=UTF-8',
    }),
    body: JSON.stringify({
      requests: [
        {
          indexName: `categories_${algoliaLocale}`,
          params: new URLSearchParams({
            hitsPerPage: '11',
            optionalWords: query,
          }).toString(),
          query,
        },
        {
          indexName: `products_${algoliaLocale}`,
          params: new URLSearchParams({
            hitsPerPage: window.innerHeight > 768 ? '8' : '6',
            optionalWords: query,
          }).toString(),
          query,
        },
        {
          indexName: 'brands',
          params: new URLSearchParams({
            hitsPerPage: '6',
            optionalWords: query,
          }).toString(),
          query,
        },
      ],
    }),
  })

  const response = await res.json()

  const [
    { hits: categoryItems },
    { hits: productItems },
    { hits: brandItems },
  ] = response.results

  return {
    brands: <AlgoliaBrand[]>brandItems,
    categories: <AlgoliaCategory[]>categoryItems,
    products: <AlgoliaProduct[]>productItems,
  }
}
